"use client";

import { useId } from "react";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import { niceTicks } from "@/lib/tool-kit/stats-descriptive/ticks";

/*
 * A small general-purpose x–y chart for the regression family: points and polylines on
 * shared axes, with an HTML legend. Colours come from the colour-blind-safe chart
 * palette and a series is also told apart by marker or dash, so colour is never the only
 * cue. Every word (axis titles, legend labels, the accessible name) arrives as a prop.
 */

const WIDTH = 640;
const HEIGHT = 380;
const LEFT = 56;
const RIGHT = 20;
const TOP = 16;
const BOTTOM = 44;

export interface PlotSeries {
  kind: "points" | "line";
  xs: readonly number[];
  ys: readonly number[];
  /** Index into the chart palette. */
  color: number;
  label: string;
  dashed?: boolean;
  /** For a point series: draw squares instead of circles. */
  square?: boolean;
}

function extent(series: readonly PlotSeries[], axis: "xs" | "ys") {
  let low = Number.POSITIVE_INFINITY;
  let high = Number.NEGATIVE_INFINITY;
  for (const item of series) {
    for (const value of item[axis]) {
      if (Number.isFinite(value)) {
        low = Math.min(low, value);
        high = Math.max(high, value);
      }
    }
  }
  return { low, high };
}

export function XYPlot({
  series,
  fmt,
  label,
  xLabel,
  yLabel,
  xRange,
  yRange,
  diagonal = false,
}: {
  series: readonly PlotSeries[];
  fmt: NumberFormatter;
  label: string;
  xLabel: string;
  yLabel: string;
  /** Fix an axis (a probability axis is 0–1, say). */
  xRange?: readonly [number, number];
  yRange?: readonly [number, number];
  /** Draw the y = x reference line (an ROC chart's chance line). */
  diagonal?: boolean;
}) {
  const xs = extent(series, "xs");
  const ys = extent(series, "ys");
  const xAxis = niceTicks(xRange?.[0] ?? xs.low, xRange?.[1] ?? xs.high, 6);
  const yAxis = niceTicks(yRange?.[0] ?? ys.low, yRange?.[1] ?? ys.high, 6);
  const clipId = useId();
  const plotWidth = WIDTH - LEFT - RIGHT;
  const plotHeight = HEIGHT - TOP - BOTTOM;
  const px = (value: number) =>
    LEFT + ((value - xAxis.lo) / (xAxis.hi - xAxis.lo || 1)) * plotWidth;
  const py = (value: number) =>
    TOP +
    plotHeight -
    ((value - yAxis.lo) / (yAxis.hi - yAxis.lo || 1)) * plotHeight;

  return (
    <div className="space-y-2">
      <svg
        aria-label={label}
        className="h-auto w-full text-muted-foreground"
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      >
        <defs>
          <clipPath id={clipId}>
            <rect height={plotHeight} width={plotWidth} x={LEFT} y={TOP} />
          </clipPath>
        </defs>
        {yAxis.ticks.map((tick) => (
          <g key={`y${tick}`}>
            <line
              stroke="currentColor"
              strokeOpacity={0.2}
              x1={LEFT}
              x2={WIDTH - RIGHT}
              y1={py(tick)}
              y2={py(tick)}
            />
            <text
              dominantBaseline="middle"
              fill="currentColor"
              fontSize={12}
              textAnchor="end"
              x={LEFT - 8}
              y={py(tick)}
            >
              {formatStat(fmt, tick)}
            </text>
          </g>
        ))}
        {xAxis.ticks.map((tick) => (
          <g key={`x${tick}`}>
            <line
              stroke="currentColor"
              strokeOpacity={0.2}
              x1={px(tick)}
              x2={px(tick)}
              y1={TOP}
              y2={TOP + plotHeight}
            />
            <text
              fill="currentColor"
              fontSize={12}
              textAnchor="middle"
              x={px(tick)}
              y={TOP + plotHeight + 18}
            >
              {formatStat(fmt, tick)}
            </text>
          </g>
        ))}
        <text
          fill="currentColor"
          fontSize={12}
          textAnchor="middle"
          x={LEFT + plotWidth / 2}
          y={HEIGHT - 6}
        >
          {xLabel}
        </text>
        <text
          fill="currentColor"
          fontSize={12}
          textAnchor="middle"
          transform={`rotate(-90 14 ${TOP + plotHeight / 2})`}
          x={14}
          y={TOP + plotHeight / 2}
        >
          {yLabel}
        </text>
        {diagonal ? (
          <line
            stroke="currentColor"
            strokeDasharray="4 4"
            strokeOpacity={0.5}
            x1={px(xAxis.lo)}
            x2={px(xAxis.hi)}
            y1={py(yAxis.lo)}
            y2={py(yAxis.hi)}
          />
        ) : null}
        {series.map((item) => (
          <SeriesMarks
            clipId={clipId}
            item={item}
            key={item.label}
            px={px}
            py={py}
            yMax={yAxis.hi}
            yMin={yAxis.lo}
          />
        ))}
      </svg>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
        {series.map((item) => (
          <li className="flex items-center gap-1.5" key={item.label}>
            <span
              aria-hidden="true"
              className="inline-block h-0.5 w-5"
              style={{
                background: CHART_COLORS[item.color % CHART_COLORS.length],
              }}
            />
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SeriesMarks({
  clipId,
  item,
  px,
  py,
  yMin,
  yMax,
}: {
  clipId: string;
  item: PlotSeries;
  px: (value: number) => number;
  py: (value: number) => number;
  yMin: number;
  yMax: number;
}) {
  const color = CHART_COLORS[item.color % CHART_COLORS.length];
  if (item.kind === "line") {
    // A curve can leave the plot (an exponential past the data); clamp so the path
    // stays inside the axes instead of drawing across the labels.
    const span = (yMax - yMin) * 2;
    const points = item.xs
      .map((x, i) => {
        const y = Math.min(yMax + span, Math.max(yMin - span, item.ys[i]));
        return Number.isFinite(y)
          ? `${px(x).toFixed(1)},${py(y).toFixed(1)}`
          : "";
      })
      .filter((point) => point !== "")
      .join(" ");
    return (
      <polyline
        clipPath={`url(#${clipId})`}
        fill="none"
        points={points}
        stroke={color}
        strokeDasharray={item.dashed ? "6 4" : undefined}
        strokeLinejoin="round"
        strokeWidth={2.5}
      />
    );
  }
  return (
    <g fill={color} fillOpacity={0.85}>
      {item.xs.map((x, i) =>
        item.square ? (
          <rect
            height={9}
            // Points can coincide, so position is the only identity they have.
            // biome-ignore lint/suspicious/noArrayIndexKey: data points by position
            key={i}
            width={9}
            x={px(x) - 4.5}
            y={py(item.ys[i]) - 4.5}
          />
        ) : (
          <circle
            cx={px(x)}
            cy={py(item.ys[i])}
            // biome-ignore lint/suspicious/noArrayIndexKey: data points by position
            key={i}
            r={4.5}
          />
        )
      )}
    </g>
  );
}
